import { Formik, FormikHelpers as FormikActions } from 'formik';
import { History } from 'history';
import React, { useState } from 'react';

import ErrorDialog from 'components/ErrorDialog/ErrorDialog';
import WarningDialog from 'components/WarningDialog/WarningDialog';

import { IDatacenterValues } from 'features/programmatic/datacenter/components/DatacenterTabsForm/DatacenterFormValues';
import DatacenterTabsForm, {
  datacenterButtons,
} from 'features/programmatic/datacenter/components/DatacenterTabsForm/DatacenterTabsForm';
import DataCenterFormValidation from 'features/programmatic/datacenter/validations/datacenters';

import {
  GET_ALL_AFFECTED_ENTITIES,
  IGetAffectedEntitiesResponse,
} from 'graphql/common/queries';

import useError from 'hooks/Error/useError';
import useLazyQuery from 'hooks/LazyQuery/useLazyQuery';
import useWarning from 'hooks/Warning/useWarning';

import { IFieldUpdate, IFieldValidate, IFormValues } from 'interfaces';
import {
  DataCenterLegacy,
  EntityType,
  Region,
  TechnicalProviderLegacy,
  UpdateDataCenterLegacyMutationFn,
  useUpdateDataCenterLegacyMutation,
} from 'interfaces/generated.types';

import {
  differenceInValues,
  handleFieldUpdate,
  handleFieldValidate,
  handleFormErrors,
} from 'utils/forms';
import { handleOnJourneyRouting } from 'utils/journeys';
import numbro from 'numbro';
import { numbroDecimalFormatting } from 'utils/numbers';
import usePreviousLocation from 'hooks/PreviousLocation/usePreviousLocation';

interface IUpdateDatacenterContainerProps {
  history: History;
  match: { params: { datacenterId: string } };
  datacenter: DataCenterLegacy;
  hasEditPermissions: boolean;
}

export const handleOnComplete = (
  history: History,
  selectedJourney: string,
  id: string,
  technicalProviderId: string
) =>
  handleOnJourneyRouting({
    history,
    selectedJourney,
    id,
    parentId: technicalProviderId,
    listEntity:
      selectedJourney === '/programmatic-connector/PARENT_ID'
        ? 'Datacenter'
        : '',
  });

export const submitUpdateDatacenter = (
  updateDataCenter: UpdateDataCenterLegacyMutationFn,
  initialValues: IDatacenterValues,
  id: string,
  toggleErrorModal: () => void,
  setSelectedJourney: any
) => (
  formValues: IDatacenterValues,
  { setStatus, setSubmitting }: FormikActions<IDatacenterValues>
) => {
  setSelectedJourney(formValues.journey);

  const filteredValues = {
    ...differenceInValues(
      { ...formValues, journey: null },
      { ...initialValues, journey: null }
    ),
    id: formValues.id,
  } as IFormValues;

  updateDataCenter({
    variables: {
      ...filteredValues,
      id,
    },
  }).catch((error) =>
    handleFormErrors({
      error,
      toggleErrorModal,
      setStatus,
      setSubmitting,
    })
  );
};

const UpdateDatacenterContainer = ({
  history,
  match,
  datacenter,
  hasEditPermissions,
}: IUpdateDatacenterContainerProps) => {
  const { hasError, toggleErrorModal, errorMessages } = useError([
    "Something went wrong and we couldn't update the Datacenter.",
    'Please try again later.',
  ]);
  const [selectedJourney, setSelectedJourney] = useState('');
  const {
    hasWarning,
    handleWarningContinue,
    toggleWarningModal,
    setWarningModal,
  } = useWarning();

  const [updateDataCenter] = useUpdateDataCenterLegacyMutation({
    onCompleted() {
      handleOnComplete(
        history,
        selectedJourney,
        datacenter.id,
        (datacenter.technicalProvider as TechnicalProviderLegacy).id
      );
    },
  });
  const validateDataCenter = useLazyQuery<IGetAffectedEntitiesResponse>(
    GET_ALL_AFFECTED_ENTITIES
  );

  const initialValues = {
    id: datacenter.id,
    name: datacenter.name as string,
    region: datacenter.region as Region,
    bidUrl: datacenter.bidUrl,
    maxQps:
      typeof datacenter.maxQps === 'number'
        ? numbro(datacenter.maxQps).format(numbroDecimalFormatting)
        : '',
    altId: datacenter.altId,
    journey: datacenterButtons[0].url,
    status: datacenter.status,
    technicalProviderName: (datacenter.technicalProvider as TechnicalProviderLegacy)
      .name as string,
    technicalProviderId: (datacenter.technicalProvider as TechnicalProviderLegacy).id,
  };

  const location = usePreviousLocation();

  return (
    <>
      <Formik<IDatacenterValues>
        initialValues={initialValues}
        validationSchema={DataCenterFormValidation}
        onSubmit={submitUpdateDatacenter(
          updateDataCenter,
          initialValues,
          match.params.datacenterId,
          toggleErrorModal,
          setSelectedJourney
        )}
      >
        {(formikProps) => (
          <DatacenterTabsForm
            {...formikProps}
            update
            history={history}
            goBackTo={{
              pathname: `/programmatic-connector/${
                (datacenter.technicalProvider as TechnicalProviderLegacy).id
              }`,
              state: location.state?.parent || {},
            }}
            hasEditPermissions={hasEditPermissions}
            technicalProviderStatus={
              (datacenter.technicalProvider as TechnicalProviderLegacy).status
            }
            onFieldValidate={({
              entity,
              handleContinue,
              handleCancel,
            }: IFieldValidate) =>
              handleFieldValidate({
                validate: validateDataCenter,
                entity: {
                  ...entity,
                  id: formikProps.values.id,
                  type: EntityType.DataCenter,
                },
                toggleErrorModal,
                setWarningModal,
                setSubmitting: formikProps.setSubmitting,
                setStatus: formikProps.setStatus,
                handleContinue,
                handleCancel,
              })
            }
            onFieldUpdate={({ entity }: IFieldUpdate) =>
              handleFieldUpdate({
                entity: { ...entity, id: formikProps.values.id },
                update: updateDataCenter,
                toggleErrorModal,
                setSubmitting: formikProps.setSubmitting,
                setStatus: formikProps.setStatus,
              })
            }
          />
        )}
      </Formik>
      <ErrorDialog
        content={{
          title: 'Error',
          closeButton: 'Close',
        }}
        isOpen={hasError}
        handleClose={toggleErrorModal}
        dataTc="updateDatacenter"
        errorMessages={errorMessages}
      />
      <WarningDialog
        handleContinue={handleWarningContinue}
        handleClose={toggleWarningModal}
        isOpen={hasWarning}
        dataTc="updateDatacenterWarningDialog"
      />
    </>
  );
};

export default UpdateDatacenterContainer;
